<script lang="ts">
	import * as Dialog from '$shared/components/ui/dialog/index.js';
	import { onMount } from 'svelte';
	import { goto } from '$app/navigation';

	interface SearchResult {
		url: string;
		meta: { title?: string };
		excerpt: string;
	}

	interface Pagefind {
		init: () => Promise<void>;
		search: (query: string) => Promise<{ results: { data: () => Promise<SearchResult> }[] }>;
	}

	let open = $state(false);
	let query = $state('');
	let results = $state<SearchResult[]>([]);
	let loading = $state(false);
	let pagefind = $state<Pagefind | null>(null);
	let selectedIndex = $state(-1);

	onMount(() => {
		function handleKeydown(e: KeyboardEvent) {
			if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
				e.preventDefault();
				open = true;
			}
		}
		document.addEventListener('keydown', handleKeydown);
		loadPagefind();

		return () => document.removeEventListener('keydown', handleKeydown);
	});

	function handleDialogKeydown(e: KeyboardEvent) {
		if (results.length === 0) return;

		if (e.key === 'ArrowDown') {
			e.preventDefault();
			selectedIndex = selectedIndex < results.length - 1 ? selectedIndex + 1 : 0;
		} else if (e.key === 'ArrowUp') {
			e.preventDefault();
			selectedIndex = selectedIndex > 0 ? selectedIndex - 1 : results.length - 1;
		} else if (e.key === 'Enter' && selectedIndex >= 0) {
			e.preventDefault();
			selectResult(results[selectedIndex].url);
		}
	}

	// Reset selection when results change
	$effect(() => {
		if (results.length > 0) {
			selectedIndex = 0;
		} else {
			selectedIndex = -1;
		}
	});

	async function loadPagefind() {
		if (typeof window === 'undefined') return;

		try {
			const pagefindPath = '/pagefind/pagefind.js';
			pagefind = await import(/* @vite-ignore */ pagefindPath);
			await pagefind?.init();
		} catch {
			console.log('Pagefind not available - run pnpm build to enable search');
		}
	}

	async function search(q: string) {
		if (!pagefind || !q.trim()) {
			results = [];
			return;
		}

		loading = true;
		try {
			const searchResults = await pagefind.search(q);
			const data = await Promise.all(searchResults.results.slice(0, 8).map((r) => r.data()));
			results = data;
		} catch (err) {
			console.error('Search error:', err);
			results = [];
		} finally {
			loading = false;
		}
	}

	function selectResult(url: string) {
		open = false;
		query = '';
		results = [];
		// Strip .html extension from Pagefind URLs for SvelteKit routing
		const cleanUrl = url.replace(/\.html$/, '');
		goto(cleanUrl);
	}

	let searchTimeout: ReturnType<typeof setTimeout>;
	$effect(() => {
		const q = query;
		clearTimeout(searchTimeout);
		searchTimeout = setTimeout(() => search(q), 200);
		return () => clearTimeout(searchTimeout);
	});
</script>

<!-- Search trigger button -->
<button
	onclick={() => (open = true)}
	class="w-full flex items-center gap-2 px-3 py-2 text-left text-[13px] text-[#878787] bg-white border border-[#e0e0e0] hover:border-[#ccc] rounded-md transition-colors"
>
	<svg class="w-4 h-4" viewBox="0 0 16 16" fill="none">
		<circle cx="7" cy="7" r="4.5" stroke="currentColor" stroke-width="1.5" />
		<path d="M10.5 10.5L14 14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
	</svg>
	<span class="flex-1">Search docs...</span>
	<kbd class="hidden sm:inline font-mono text-[11px] px-1.5 py-0.5 bg-white border rounded text-[#878787]">
		<span class="text-[10px]">⌘</span>K
	</kbd>
</button>

<!-- Search dialog -->
<Dialog.Root bind:open>
	<Dialog.Content class="sm:max-w-lg p-0 gap-0">
		<div class="flex items-center border-b px-4">
			<svg class="w-4 h-4 text-[#878787] shrink-0" viewBox="0 0 16 16" fill="none">
				<circle cx="7" cy="7" r="4.5" stroke="currentColor" stroke-width="1.5" />
				<path d="M10.5 10.5L14 14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
			</svg>
			<!-- svelte-ignore a11y_autofocus -->
			<input
				bind:value={query}
				onkeydown={handleDialogKeydown}
				placeholder="Search documentation..."
				class="flex-1 px-3 py-4 text-[15px] bg-transparent outline-none placeholder:text-[#878787]"
				autofocus
			/>
			{#if loading}
				<div class="w-4 h-4 border-2 border-[#e5e5e5] border-t-[#ee6018] rounded-full animate-spin"></div>
			{/if}
		</div>

		<div class="results-container max-h-[400px] overflow-y-auto">
			{#if !pagefind}
				<div class="p-8 text-center text-[14px] text-[#878787]">
					<p>Search is available after building the site.</p>
					<p class="mt-1 text-[12px]">Run <code class="font-mono bg-[#f5f5f5] px-1 py-0.5 rounded">pnpm build</code> to enable search.</p>
				</div>
			{:else if query.trim() && results.length === 0 && !loading}
				<div class="p-8 text-center text-[14px] text-[#878787]">
					No results found for "{query}"
				</div>
			{:else if results.length > 0}
				<ul class="py-2">
					{#each results as result, i}
						<li>
							<button
								onclick={() => selectResult(result.url)}
								class="w-full text-left px-4 py-3 transition-colors {i === selectedIndex ? 'bg-[#f0ebe4]' : 'hover:bg-[#f5f5f5]'}"
							>
								<div class="font-medium text-[14px] text-[#020202]">
									{result.meta.title || 'Untitled'}
								</div>
								<div class="mt-1 text-[13px] text-[#5c5855] line-clamp-2">
									{@html result.excerpt}
								</div>
							</button>
						</li>
					{/each}
				</ul>
			{:else if !query.trim()}
				<div class="p-8 text-center text-[14px] text-[#878787]">
					Start typing to search the documentation
				</div>
			{/if}
		</div>

		<div class="flex items-center justify-between px-4 py-2 border-t text-[11px] text-[#878787]">
			<span>
				<kbd class="font-mono px-1 py-0.5 bg-[#f5f5f5] rounded">↑↓</kbd> to navigate
				<kbd class="font-mono px-1 py-0.5 bg-[#f5f5f5] rounded ml-2">↵</kbd> to select
			</span>
			<span>
				<kbd class="font-mono px-1 py-0.5 bg-[#f5f5f5] rounded">esc</kbd> to close
			</span>
		</div>
	</Dialog.Content>
</Dialog.Root>

<style>
	.results-container {
		scrollbar-width: thin;
		scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
	}

	.results-container::-webkit-scrollbar {
		width: 6px;
	}

	.results-container::-webkit-scrollbar-track {
		background: transparent;
	}

	.results-container::-webkit-scrollbar-thumb {
		background-color: rgba(0, 0, 0, 0.2);
		border-radius: 3px;
	}

	.results-container::-webkit-scrollbar-thumb:hover {
		background-color: rgba(0, 0, 0, 0.3);
	}
</style>
